"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type Allocation,
  type AssignmentMethod,
  type AssignmentProblem,
  assignParticipants,
  balanceTable,
  distinctArms,
  MAX_PARTICIPANTS,
  numberedParticipants,
  parseArms,
  parseBlockSizes,
  parseParticipants,
} from "@/lib/tool-kit/research-design/assignment";
import type { NumberFormatter } from "@/lib/tool-kit/research-design/format";
import { toCsv, toTsv } from "@/lib/tool-kit/research-design/matrix";
import { freshSeed } from "@/lib/tool-kit/research-design/random";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_ASSIGNMENT_GENERATOR_STRINGS = {
  participantsLegend: "Participants",
  byCount: "Number them for me",
  byList: "Paste a list",
  countLabel: "Number of participants",
  countPlaceholder: "40",
  idPrefix: "P",
  countHint: "IDs run P001, P002, … in enrollment order.",
  listLabel: "Participant IDs — one per line, in enrollment order",
  listHint:
    "For stratified randomization, add each participant's stratum after a tab or comma (P01, Female). A header line such as ID, Sex is skipped.",
  listPlaceholder: "P01, Female\nP02, Male\nP03, Female",
  participantCount: "one: {count} participant|other: {count} participants",
  listRead: "Read {participants}.",
  strataRead: "Strata found: {strata}.",
  armsLabel: "Conditions — one per line or separated by commas",
  armsHint:
    "List a condition twice for a 2:1 ratio (Treatment, Treatment, Control).",
  defaultArms: "Control\nTreatment",
  methodLegend: "Method",
  methodSimple: "Simple",
  methodBlock: "Permuted blocks",
  methodStratified: "Stratified blocks",
  methodSimpleNote:
    "Each participant is assigned independently, like rolling a fair die. Group sizes can drift apart by chance, most of all in small studies.",
  methodBlockNote:
    "Every block contains each condition equally often, so group sizes can never differ by more than half a block.",
  methodStratifiedNote:
    "Permuted blocks run separately inside each stratum, so the groups stay balanced on the stratifying variable too.",
  blockLabel: "Block size",
  blockHint:
    "A multiple of the {k} listed conditions. Give several sizes (4, 6) and each block's size is drawn at random, which makes the sequence harder to predict.",
  defaultBlocks: "4",
  seedLabel: "Seed",
  seedPlaceholder: "Leave blank for a fresh random seed",
  seedHint:
    "The same seed with the same inputs always reproduces the same list — record it in your protocol.",
  newSeed: "New random seed",
  generate: "Generate allocation",
  results: "Allocation",
  prompt:
    "Set up the participants and conditions, then generate the allocation.",
  problemNoParticipants: "Add at least one participant.",
  problemTooMany: "This tool assigns up to {max} participants at a time.",
  problemTooFewArms: "List at least two conditions.",
  problemBlock:
    "Every block size must be a whole-number multiple of the {k} listed conditions, such as {example}.",
  problemStratum:
    "Stratified randomization needs a stratum for every participant: paste a list with each participant's stratum after a comma or tab.",
  audit: "Seed {seed} · {method} · generated {date}",
  auditBlocks: "Seed {seed} · {method}, block sizes {sizes} · generated {date}",
  balanceHeading: "Group sizes",
  colStratum: "Stratum",
  overall: "All participants",
  colTotal: "Total",
  listHeading: "Allocation list",
  colSequence: "#",
  colId: "Participant",
  colBlock: "Block",
  colArm: "Condition",
  shownRows:
    "Showing the first {shown} of {total} rows; the copy and the CSV have them all.",
  copyList: "Copy for a spreadsheet",
  copied: "Copied!",
  downloadCsv: "Download CSV",
  csvFile: "random-allocation-{seed}.csv",
  concealmentHeading: "Keep the list concealed",
  concealment:
    "Whoever enrolls participants should not be able to see or predict the next assignment. Generate the list in advance, keep it with someone not involved in recruitment, and reveal each assignment only after that participant is enrolled — for example with sequentially numbered, opaque, sealed envelopes or a central randomization service.",
} as const;

type Strings = typeof RANDOM_ASSIGNMENT_GENERATOR_STRINGS;
type Source = "count" | "list";

interface Generated {
  allocations: Allocation[];
  arms: string[];
  audit: string;
  seed: string;
}

const SHOWN_ROWS = 300;
const FILE_UNSAFE = /[^\w-]+/g;

const METHOD_KEYS = {
  simple: { name: "methodSimple", note: "methodSimpleNote" },
  block: { name: "methodBlock", note: "methodBlockNote" },
  stratified: { name: "methodStratified", note: "methodStratifiedNote" },
} as const;

function problemText(
  problem: AssignmentProblem,
  armCount: number,
  strings: Strings,
  fmt: NumberFormatter
): string {
  const texts: Record<AssignmentProblem, string> = {
    "no-participants": strings.problemNoParticipants,
    "too-many-participants": t(strings.problemTooMany, {
      max: fmt(MAX_PARTICIPANTS),
    }),
    "too-few-arms": strings.problemTooFewArms,
    "bad-block-size": t(strings.problemBlock, {
      k: fmt(armCount),
      example: fmt(Math.max(2, armCount) * 2),
    }),
    "missing-stratum": strings.problemStratum,
  };
  return texts[problem];
}

function rowsFor(result: Generated, strings: Strings): (string | number)[][] {
  const stratified = result.allocations.some((row) => row.stratum !== null);
  const blocked = result.allocations.some((row) => row.block !== null);
  const head = [
    strings.colSequence,
    strings.colId,
    ...(stratified ? [strings.colStratum] : []),
    ...(blocked ? [strings.colBlock] : []),
    strings.colArm,
  ];
  return [
    head,
    ...result.allocations.map((row) => [
      row.sequence,
      row.id,
      ...(stratified ? [row.stratum ?? ""] : []),
      ...(blocked ? [row.block ?? ""] : []),
      row.arm,
    ]),
  ];
}

function Results({ result, strings }: { result: Generated; strings: Strings }) {
  const fmt = useToolNumber();
  const names = distinctArms(result.arms);
  const balance = balanceTable(result.allocations, result.arms);
  const table = rowsFor(result, strings);
  return (
    <div className="space-y-6">
      <p className={`${RESULT_CARD} font-mono text-foreground text-sm`}>
        {result.audit}
      </p>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.balanceHeading}</h3>
        <DataTable
          head={[strings.colStratum, ...names, strings.colTotal]}
          rows={balance.map((row) => [
            row.stratum ?? strings.overall,
            ...row.counts.map((count) => fmt(count)),
            fmt(row.total),
          ])}
        />
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.listHeading}</h3>
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyList}
            text={toTsv(table)}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              downloadText(
                t(strings.csvFile, {
                  seed: result.seed.replace(FILE_UNSAFE, "-"),
                }),
                toCsv(table),
                "text/csv;charset=utf-8"
              )
            }
            type="button"
          >
            {strings.downloadCsv}
          </button>
        </div>
        <DataTable
          head={table[0].map(String)}
          rows={table
            .slice(1, SHOWN_ROWS + 1)
            .map((row) =>
              row.map((cell) => (typeof cell === "number" ? fmt(cell) : cell))
            )}
        />
        {result.allocations.length > SHOWN_ROWS ? (
          <p className={HINT}>
            {t(strings.shownRows, {
              shown: fmt(SHOWN_ROWS),
              total: fmt(result.allocations.length),
            })}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default function RandomAssignmentGeneratorWidget() {
  const strings = useToolUi(RANDOM_ASSIGNMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [source, setSource] = useState<Source>("count");
  const [countText, setCountText] = useState("20");
  const [listText, setListText] = useState("");
  const [armsText, setArmsText] = useState<string>(strings.defaultArms);
  const [method, setMethod] = useState<AssignmentMethod>("block");
  const [blockText, setBlockText] = useState<string>(strings.defaultBlocks);
  const [seed, setSeed] = useState("");
  const [result, setResult] = useState<Generated | null>(null);
  const [problem, setProblem] = useState<AssignmentProblem | null>(null);

  const arms = parseArms(armsText);
  const count = parseIntegerInput(countText);
  const participants =
    source === "count"
      ? numberedParticipants(
          Math.min(Math.max(count ?? 0, 0), MAX_PARTICIPANTS + 1),
          strings.idPrefix
        )
      : parseParticipants(listText);
  const strata = [
    ...new Set(
      participants.flatMap((row) => (row.stratum ? [row.stratum] : []))
    ),
  ];

  function edit<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setResult(null);
      setProblem(null);
    };
  }

  function generate() {
    const seedUsed = seed.trim() === "" ? freshSeed() : seed.trim();
    setSeed(seedUsed);
    const blockSizes = parseBlockSizes(blockText);
    const outcome = assignParticipants({
      participants,
      arms,
      method,
      blockSizes,
      seed: seedUsed,
    });
    if (!outcome.ok) {
      setProblem(outcome.problem);
      setResult(null);
      return;
    }
    const date = new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date());
    const name = strings[METHOD_KEYS[method].name];
    setProblem(null);
    setResult({
      allocations: outcome.allocations,
      arms,
      seed: seedUsed,
      audit:
        method === "simple"
          ? t(strings.audit, { seed: seedUsed, method: name, date })
          : t(strings.auditBlocks, {
              seed: seedUsed,
              method: name,
              sizes: blockSizes.map((size) => fmt(size)).join(", "),
              date,
            }),
    });
  }

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.participantsLegend}
          name="ra-source"
          onChange={edit(setSource)}
          options={[
            { value: "count", label: strings.byCount },
            { value: "list", label: strings.byList },
          ]}
          value={source}
        />
        {source === "count" ? (
          <div className="max-w-xs">
            <TextField
              hint={strings.countHint}
              id="ra-count"
              label={strings.countLabel}
              numeric
              onChange={edit(setCountText)}
              placeholder={strings.countPlaceholder}
              value={countText}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <TextAreaField
              hint={strings.listHint}
              id="ra-list"
              label={strings.listLabel}
              onChange={edit(setListText)}
              placeholder={strings.listPlaceholder}
              rows={6}
              value={listText}
            />
            <p className={HINT}>
              {t(strings.listRead, {
                participants: tPlural(
                  strings.participantCount,
                  locale,
                  participants.length,
                  { count: fmt(participants.length) }
                ),
              })}{" "}
              {strata.length > 0
                ? t(strings.strataRead, { strata: strata.join(", ") })
                : null}
            </p>
          </div>
        )}
        <TextAreaField
          hint={strings.armsHint}
          id="ra-arms"
          label={strings.armsLabel}
          onChange={edit(setArmsText)}
          prose
          rows={3}
          value={armsText}
        />
        <PillGroup
          label={strings.methodLegend}
          name="ra-method"
          onChange={edit(setMethod)}
          options={(["simple", "block", "stratified"] as const).map(
            (value) => ({ value, label: strings[METHOD_KEYS[value].name] })
          )}
          value={method}
        />
        <p className={HINT}>{strings[METHOD_KEYS[method].note]}</p>
        {method === "simple" ? null : (
          <div className="max-w-xs">
            <TextField
              hint={t(strings.blockHint, { k: fmt(arms.length) })}
              id="ra-blocks"
              label={strings.blockLabel}
              mono
              onChange={edit(setBlockText)}
              value={blockText}
            />
          </div>
        )}
        <div className="space-y-2">
          <TextField
            hint={strings.seedHint}
            id="ra-seed"
            label={strings.seedLabel}
            mono
            onChange={edit(setSeed)}
            placeholder={strings.seedPlaceholder}
            value={seed}
          />
          <button
            className={BUTTON_SMALL}
            onClick={() => edit(setSeed)(freshSeed())}
            type="button"
          >
            {strings.newSeed}
          </button>
        </div>
        <button className={BUTTON_PRIMARY} onClick={generate} type="button">
          {strings.generate}
        </button>
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {problem ? (
          <ErrorNotice>
            {problemText(problem, arms.length, strings, fmt)}
          </ErrorNotice>
        ) : null}
        {result ? (
          <Results result={result} strings={strings} />
        ) : (
          <Notice>{strings.prompt}</Notice>
        )}
        <div className="space-y-1">
          <h3 className={WIDGET_H3}>{strings.concealmentHeading}</h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.concealment}
          </p>
        </div>
      </section>
    </>
  );
}
